import { useEffect, useState } from 'react';
import { getFxRate } from '../services/FxRateService';

/**
 * Returns the converted amount (in toCurrencyCode) for `amount` (in
 * fromCurrencyCode) -- undefined while loading, on a fetch failure, or when
 * no conversion is needed (same currency, or either code missing). Purely
 * for display, e.g. "45€ (≈ $49 USD)" next to a Machinery's own host price
 * -- never used to charge anything, see FxRateService's own doc comment.
 */
export const useFxConvertedAmount = (
  amount: number | undefined,
  fromCurrencyCode: string | undefined,
  toCurrencyCode: string | undefined
): number | undefined => {
  const [converted, setConverted] = useState<number | undefined>(undefined);

  useEffect(() => {
    setConverted(undefined);
    if (
      amount == null ||
      !fromCurrencyCode ||
      !toCurrencyCode ||
      fromCurrencyCode.toUpperCase() === toCurrencyCode.toUpperCase()
    ) {
      return;
    }

    let cancelled = false;
    getFxRate(fromCurrencyCode, toCurrencyCode)
      .then((rate) => {
        if (!cancelled) setConverted(amount * rate);
      })
      .catch(() => {
        // Silently omit the secondary approximation -- the host's own
        // price (already rendered regardless) stays the source of truth.
      });
    return () => {
      cancelled = true;
    };
  }, [amount, fromCurrencyCode, toCurrencyCode]);

  return converted;
};
